import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Package, Loader, AlertCircle, RefreshCw, Clock } from 'lucide-react';
import { pOrdersService, IncompleteReceiptLine, StaleArrivedLine } from '../../../../services/pOrdersService';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { NumericInput } from '../../../common/inputs';

interface IncompleteReceiptReportProps {
  onBack?: () => void;
}

type ReportTab = 'incomplete' | 'stale';

/**
 * Mottaksrapporter (read-only), to faner:
 * - Punkt 17 (Norlux): Ufullstendige mottak — ordrelinjer der bestilt antall er større enn
 *   ankomst-registrert antall (StcTrans LineStatus >= 20 = ankommet + mottatt).
 * - Gamle ankommet-linjer — StcTrans på LineStatus 20 eldre enn N dager som aldri ble
 *   fullført/slettet. Slike linjer blåser opp «Ankommet» og gir misvisende overmottak-advarsler.
 */
export const IncompleteReceiptReport: React.FC<IncompleteReceiptReportProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<ReportTab>('incomplete');

  // Fane 1: ufullstendige mottak
  const [lines, setLines] = useState<IncompleteReceiptLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [supplierNo, setSupplierNo] = useState('');
  const [warehouse, setWarehouse] = useState('');

  // Fane 2: gamle ankommet-linjer
  const [staleLines, setStaleLines] = useState<StaleArrivedLine[]>([]);
  const [staleLoading, setStaleLoading] = useState(false);
  const [staleError, setStaleError] = useState<string | null>(null);
  const [staleLoaded, setStaleLoaded] = useState(false);
  const [olderThanDays, setOlderThanDays] = useState('3');
  const [staleWarehouse, setStaleWarehouse] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await pOrdersService.getIncompleteReceipts({
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        supplierNo: supplierNo || undefined,
        warehouse: warehouse || undefined,
      });
      setLines(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('warehouse.modules.receipt.reports.loadFailed', {}, 'Kunne ikke laste rapporten'));
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, supplierNo, warehouse, t]);

  const loadStale = useCallback(async () => {
    setStaleLoading(true);
    setStaleError(null);
    try {
      const data = await pOrdersService.getStaleArrivedLines({
        olderThanDays: olderThanDays === '' ? undefined : parseInt(olderThanDays, 10),
        warehouse: staleWarehouse || undefined,
      });
      setStaleLines(data);
      setStaleLoaded(true);
    } catch (err) {
      setStaleError(err instanceof Error ? err.message : t('warehouse.modules.receipt.reports.loadFailed', {}, 'Kunne ikke laste rapporten'));
    } finally {
      setStaleLoading(false);
    }
  }, [olderThanDays, staleWarehouse, t]);

  // Last ved oppstart (filtre kjøres via "Søk"-knappen).
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Last fane 2 første gang den åpnes.
  useEffect(() => {
    if (tab === 'stale' && !staleLoaded && !staleLoading) {
      loadStale();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const fmtQty = (n: number) => (Math.round((n || 0) * 1000) / 1000).toString();
  const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString('nb-NO') : '');

  const activeCount = tab === 'incomplete' ? lines.length : staleLines.length;
  const refresh = tab === 'incomplete' ? load : loadStale;

  const tabButtonClass = (isActive: boolean) =>
    `px-3 sm:px-4 py-2 text-sm font-medium rounded-lg min-h-[40px] transition-colors ${
      isActive ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
    }`;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-3 sm:px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 text-gray-500 hover:text-gray-700 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
                {tab === 'incomplete'
                  ? t('warehouse.modules.receipt.reports.incompleteReceipts', {}, 'Ufullstendige mottak')
                  : t('warehouse.modules.receipt.reports.staleArrivedLines', {}, 'Gamle ankommet-linjer')}
              </h1>
            </div>
          </div>
          <button
            onClick={refresh}
            className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1 min-h-[44px] px-2"
          >
            <RefreshCw className="w-4 h-4" /> {t('common.buttons.refresh', {}, 'Oppdater')}
          </button>
        </div>

        {/* Faner */}
        <div className="mt-3 flex gap-2">
          <button onClick={() => setTab('incomplete')} className={tabButtonClass(tab === 'incomplete')}>
            {t('warehouse.modules.receipt.reports.incompleteReceipts', {}, 'Ufullstendige mottak')}
          </button>
          <button onClick={() => setTab('stale')} className={tabButtonClass(tab === 'stale')}>
            {t('warehouse.modules.receipt.reports.staleArrivedLines', {}, 'Gamle ankommet-linjer')}
          </button>
        </div>

        {/* Filtre */}
        {tab === 'incomplete' ? (
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <label className="flex flex-col text-xs text-gray-500">
              {t('warehouse.modules.receipt.reports.fromDate', {}, 'Fra dato')}
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                className="mt-0.5 px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
            </label>
            <label className="flex flex-col text-xs text-gray-500">
              {t('warehouse.modules.receipt.reports.toDate', {}, 'Til dato')}
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                className="mt-0.5 px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
            </label>
            <label className="flex flex-col text-xs text-gray-500">
              {t('warehouse.modules.receipt.reports.supplier', {}, 'Leverandør')}
              <input type="text" value={supplierNo} onChange={(e) => setSupplierNo(e.target.value)}
                className="mt-0.5 px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
            </label>
            <label className="flex flex-col text-xs text-gray-500">
              {t('warehouse.modules.receipt.reports.warehouse', {}, 'Lagersted')}
              <input type="text" value={warehouse} onChange={(e) => setWarehouse(e.target.value)}
                className="mt-0.5 px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
            </label>
            <button
              onClick={load}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 min-h-[40px]"
            >
              {t('common.buttons.search', {}, 'Søk')}
            </button>
          </div>
        ) : (
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <label className="flex flex-col text-xs text-gray-500">
              {t('warehouse.modules.receipt.reports.olderThanDays', {}, 'Eldre enn (dager)')}
              <NumericInput mode="integer" value={olderThanDays} onChange={(e) => setOlderThanDays(e.target.value)}
                className="mt-0.5 px-2 py-1.5 border border-gray-300 rounded-lg text-sm w-24" />
            </label>
            <label className="flex flex-col text-xs text-gray-500">
              {t('warehouse.modules.receipt.reports.warehouse', {}, 'Lagersted')}
              <input type="text" value={staleWarehouse} onChange={(e) => setStaleWarehouse(e.target.value)}
                className="mt-0.5 px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
            </label>
            <button
              onClick={loadStale}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 min-h-[40px]"
            >
              {t('common.buttons.search', {}, 'Søk')}
            </button>
          </div>
        )}

        <div className="mt-2 text-sm text-gray-600">
          <span className="font-medium">{activeCount}</span> {t('warehouse.modules.receipt.reports.lines', {}, 'linjer')}
        </div>

        {tab === 'stale' && (
          <p className="mt-1 text-xs text-gray-500">
            {t('warehouse.modules.receipt.reports.staleArrivedDescription', {}, 'Ankomst-registreringer som aldri ble fullført eller slettet. Disse blåser opp «Ankommet»-tallet på ordren og gir misvisende overmottak-advarsler til de ryddes.')}
          </p>
        )}
      </div>

      <div className="p-3 sm:p-4">
        {tab === 'incomplete' ? (
          loading ? (
            <div className="flex items-center justify-center h-48"><Loader className="w-8 h-8 text-blue-600 animate-spin" /></div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 break-words">{error}</p>
            </div>
          ) : lines.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
              <Package className="w-10 h-10 mx-auto mb-3 text-gray-400" />
              <p className="font-medium">{t('warehouse.modules.receipt.reports.noIncompleteReceipts', {}, 'Ingen ufullstendige mottak funnet')}</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                    <th className="px-3 py-2">{t('warehouse.modules.receipt.reports.order', {}, 'Ordre')}</th>
                    <th className="px-3 py-2">{t('warehouse.modules.receipt.reports.line', {}, 'Linje')}</th>
                    <th className="px-3 py-2">{t('warehouse.general.itemNumber', {}, 'Varenr')}</th>
                    <th className="px-3 py-2">{t('common.fields.description', {}, 'Beskrivelse')}</th>
                    <th className="px-3 py-2 text-right">{t('warehouse.modules.receipt.reports.ordered', {}, 'Bestilt')}</th>
                    <th className="px-3 py-2 text-right">{t('warehouse.modules.receipt.reports.registered', {}, 'Ankommet')}</th>
                    <th className="px-3 py-2 text-right">{t('warehouse.modules.receipt.reports.remaining', {}, 'Rest')}</th>
                    <th className="px-3 py-2">{t('warehouse.modules.receipt.reports.supplier', {}, 'Leverandør')}</th>
                    <th className="px-3 py-2">{t('warehouse.modules.receipt.reports.orderDate', {}, 'Ordredato')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {lines.map((l) => (
                    <tr key={`${l.orderNo}-${l.lineNo}`} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">{l.orderNoERP || l.orderNo}</td>
                      <td className="px-3 py-2 text-gray-600">{l.lineNo}</td>
                      <td className="px-3 py-2 text-gray-900 whitespace-nowrap">{l.itemNo}</td>
                      <td className="px-3 py-2 text-gray-600">{l.description1}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">{fmtQty(l.orderedQuantity)} {l.itemUnitTxt}</td>
                      <td className="px-3 py-2 text-right">{fmtQty(l.registeredQuantity)}</td>
                      <td className="px-3 py-2 text-right font-medium text-amber-700">{fmtQty(l.remainingQuantity)}</td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{l.supplierName || l.supplierNo}</td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{fmtDate(l.orderDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          staleLoading ? (
            <div className="flex items-center justify-center h-48"><Loader className="w-8 h-8 text-blue-600 animate-spin" /></div>
          ) : staleError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 break-words">{staleError}</p>
            </div>
          ) : staleLines.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
              <Clock className="w-10 h-10 mx-auto mb-3 text-gray-400" />
              <p className="font-medium">{t('warehouse.modules.receipt.reports.noStaleArrivedLines', {}, 'Ingen gamle ankommet-linjer funnet')}</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                    <th className="px-3 py-2">{t('warehouse.modules.receipt.reports.order', {}, 'Ordre')}</th>
                    <th className="px-3 py-2">{t('warehouse.modules.receipt.reports.line', {}, 'Linje')}</th>
                    <th className="px-3 py-2">{t('warehouse.general.itemNumber', {}, 'Varenr')}</th>
                    <th className="px-3 py-2">{t('common.fields.description', {}, 'Beskrivelse')}</th>
                    <th className="px-3 py-2 text-right">{t('common.fields.quantity', {}, 'Antall')}</th>
                    <th className="px-3 py-2">{t('warehouse.modules.receipt.reports.registeredDate', {}, 'Registrert')}</th>
                    <th className="px-3 py-2 text-right">{t('warehouse.modules.receipt.reports.daysOld', {}, 'Dager')}</th>
                    <th className="px-3 py-2">{t('warehouse.modules.receipt.reports.operator', {}, 'Operatør')}</th>
                    <th className="px-3 py-2">{t('warehouse.modules.receipt.reports.location', {}, 'Lokasjon')}</th>
                    <th className="px-3 py-2">{t('warehouse.modules.receipt.reports.supplier', {}, 'Leverandør')}</th>
                    <th className="px-3 py-2">{t('warehouse.modules.receipt.reports.batch', {}, 'Batch')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {staleLines.map((l) => (
                    <tr key={l.stcTransId} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">{l.orderNoERP || l.orderNo}</td>
                      <td className="px-3 py-2 text-gray-600">{l.lineNo}</td>
                      <td className="px-3 py-2 text-gray-900 whitespace-nowrap">{l.itemNo}</td>
                      <td className="px-3 py-2 text-gray-600">{l.description1}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">{fmtQty(l.quantity)} {l.itemUnitTxt}</td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{fmtDate(l.created)}</td>
                      <td className="px-3 py-2 text-right">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                          l.daysOld >= 7 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {l.daysOld}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{l.createdBy}</td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{l.locationName}</td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{l.supplierName || l.supplierNo}</td>
                      <td className="px-3 py-2 text-gray-400 text-xs whitespace-nowrap" title={l.batchID}>{l.batchID}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default IncompleteReceiptReport;
